import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Tenant {
  id: string
  name: string
  slug: string
  domain: string
  custom_domain: string | null
  status: string
  plan_tier: string
  users_count: number
  users_limit: number
  storage_used_gb: number
  storage_limit_gb: number
  api_calls_month: number
  api_calls_limit: number
  features_enabled: string[]
  created_at: string
  trial_expires_at?: string
  suspended_reason?: string
}

export default function MultiTenancyPage() {
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [tenantsRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/multi-tenancy/tenants'),
      fetch(API_BASE + '/multi-tenancy/statistics')
    ])
    if (tenantsRes.ok) setTenants((await tenantsRes.json()).tenants || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'trial': return 'bg-blue-100 text-blue-800'
      case 'suspended': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case 'enterprise': return 'bg-purple-100 text-purple-800'
      case 'professional': return 'bg-blue-100 text-blue-800'
      case 'starter': return 'bg-green-100 text-green-800'
      case 'trial': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return (num / 1000).toFixed(0) + 'K'
    return num.toString()
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Multi-Tenancy Management</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Tenant</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Tenants</div>
              <div className="text-2xl font-bold">{statistics.total_tenants}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active</div>
              <div className="text-2xl font-bold text-green-600">{statistics.active_tenants}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Trial</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.trial_tenants}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Users</div>
              <div className="text-2xl font-bold">{statistics.total_users}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Storage Used</div>
              <div className="text-2xl font-bold text-purple-600">{statistics.total_storage_gb?.toFixed(0)} GB</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Tenants</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Users</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Storage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {tenants.map(tenant => (
                  <tr key={tenant.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedTenant(tenant)}>
                    <td className="px-6 py-4">
                      <div className="font-medium">{tenant.name}</div>
                      <div className="text-sm text-gray-500">{tenant.domain}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getPlanColor(tenant.plan_tier)}`}>{tenant.plan_tier}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div>{tenant.users_count}/{tenant.users_limit}</div>
                      <div className="w-20 h-1 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full" style={{ width: `${(tenant.users_count / tenant.users_limit) * 100}%` }}></div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>{tenant.storage_used_gb.toFixed(1)} / {tenant.storage_limit_gb} GB</div>
                      <div className="w-20 h-1 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-purple-500 rounded-full" style={{ width: `${(tenant.storage_used_gb / tenant.storage_limit_gb) * 100}%` }}></div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(tenant.status)}`}>{tenant.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {selectedTenant && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold">{selectedTenant.name}</h3>
                <span className={`px-2 py-1 rounded text-xs ${getStatusColor(selectedTenant.status)}`}>{selectedTenant.status}</span>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-500">Domain</div>
                  <div className="font-medium">{selectedTenant.domain}</div>
                  {selectedTenant.custom_domain && (
                    <div className="text-sm text-blue-600">{selectedTenant.custom_domain}</div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-blue-50 rounded">
                    <div className="text-2xl font-bold text-blue-600">{formatNumber(selectedTenant.api_calls_month)}</div>
                    <div className="text-xs text-gray-500">API calls this month</div>
                  </div>
                  <div className="p-3 bg-purple-50 rounded">
                    <div className="text-2xl font-bold text-purple-600">{selectedTenant.storage_used_gb.toFixed(1)}</div>
                    <div className="text-xs text-gray-500">GB storage used</div>
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-500 mb-2">Features Enabled</div>
                  <div className="flex flex-wrap gap-1">
                    {selectedTenant.features_enabled.map((feature, i) => (
                      <span key={i} className="px-2 py-0.5 bg-gray-100 rounded text-xs">{feature.replace(/_/g, ' ')}</span>
                    ))}
                  </div>
                </div>

                {selectedTenant.suspended_reason && (
                  <div className="p-3 bg-red-50 rounded">
                    <div className="text-sm font-medium text-red-800">Suspended</div>
                    <div className="text-sm text-red-600">{selectedTenant.suspended_reason}</div>
                  </div>
                )}

                <div className="text-xs text-gray-500">
                  Created: {new Date(selectedTenant.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
